import type {
  GetFormFieldOptionsByParentTypeQuery,
  Parent_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import _ from 'lodash';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import type { ZodSchema } from 'zod';
import { z } from 'zod';

import { useConditionalFieldsContext } from '../../conditional-fields-provider/ConditionalFieldsContext';
import {
  getHiddenFieldIds,
  hasFieldId,
} from '../../conditional-fields-provider/hiddenFieldIds';
import { buildLowercaseIndex } from '../../conditionPropertyKeys';
import { useCustomisableFormDataContext } from '../../customisable-form-data/CustomisableFormDataContext';
import { resolveConfiguredRequired } from '../../resolveConfiguredRequired';
import { useRiskSmartForm } from '../RiskSmartFormContext';

const checkRequired = (val: unknown) => {
  if (Array.isArray(val)) {
    return val.length > 0;
  }
  if (typeof val === 'string') {
    return val.length > 0;
  }

  return !_.isNil(val);
};

export const getRequiredFields = ({
  allFieldIds,
  defaultRequiredFields,
  fieldConfig,
  hiddenFields,
}: {
  allFieldIds: string[];
  defaultRequiredFields: string[];
  fieldConfig: GetFormFieldOptionsByParentTypeQuery['form_field_configuration'];
  hiddenFields: Set<string>;
}) => {
  const requiredFields = new Set<string>();

  const fieldIdByLower = buildLowercaseIndex(allFieldIds, (id) => id);

  const configFieldIds = new Set(
    fieldConfig.map((f) => f.FieldId.toLowerCase())
  );

  // Use default required unless overridden with user settings
  defaultRequiredFields
    .filter((fieldId) => !configFieldIds.has(fieldId.toLowerCase()))
    .filter((fieldId) => !hasFieldId(hiddenFields, fieldId))
    .forEach((fieldId) => requiredFields.add(fieldId));

  // Shared resolver, so the field label derives required from this same source
  // (CUS-374). Iterate the de-duplicated ids rather than the rows so each field
  // re-scans `fieldConfig` at most once.
  for (const lowerFieldId of configFieldIds) {
    const formFieldId = fieldIdByLower.get(lowerFieldId);
    if (
      formFieldId != null &&
      resolveConfiguredRequired(fieldConfig, formFieldId, hiddenFields)
    ) {
      requiredFields.add(formFieldId);
    }
  }

  return requiredFields;
};

// Cannot remove required-ness declared in the page's own zod schema.
export const addRequireFieldsToSchema = (
  schema: ZodSchema,
  requiredFields: Set<string>
): ZodSchema => {
  if (requiredFields.size === 0) {
    return schema;
  }

  // Run required-field checks and the base schema in parallel, then merge
  // their errors. We can't use z.intersection because it would merge the two
  // parsed outputs, which conflicts with .pipe() transforms that change a
  // field's type (e.g. string→number). We can't use schema.superRefine
  // because when the base schema aborts (e.g. a field whose value has the
  // wrong type, like null where an object is expected) the refinement never
  // runs — which would suppress required errors on other fields.
  //
  // Done in a single async transform so the base schema is parsed once per
  // validation. A previous shape used .superRefine + .transform but parsed
  // twice, which would double-fire any async refinements (e.g. the
  // Title duplicate-check refetch on Control Group). safeParseAsync is
  // required because sync safeParse throws on schemas with async refines.
  return z.any().transform(async (input, ctx) => {
    // Parse base schema first so its (usually more specific) errors appear
    // before the generic "Required" errors for the same field path.
    const result = await schema.safeParseAsync(input);
    const pathsWithBaseErrors = new Set<string>();
    if (!result.success) {
      for (const issue of result.error.issues) {
        ctx.addIssue(issue);
        pathsWithBaseErrors.add(issue.path.join('.'));
      }
    }
    for (const fieldId of requiredFields) {
      // Skip if the base schema already reported an error on this field —
      // its message is more specific than a generic "Required".
      if (pathsWithBaseErrors.has(fieldId)) {
        continue;
      }
      if (!checkRequired(_.get(input, fieldId))) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Required',
          path: fieldId.split('.'),
          fatal: true,
        });
      }
    }

    // Return the base schema's transformed output (e.g. coerced numbers
    // from .pipe() transforms) so consumers like react-hook-form receive
    // the correctly typed form values when validation succeeds.
    return result.success ? result.data : input;
  });
};

export const useTransformSchemaToRequiredFields = (
  parentType: Parent_Type_Enum
) => {
  const { hiddenFields, hiddenSections } = useConditionalFieldsContext();
  const { setCustomFormValidation, defaultRequiredFields, allFieldIds } =
    useRiskSmartForm();
  const { formFieldConfigurations, formSections } =
    useCustomisableFormDataContext();

  const { control } = useFormContext();

  useEffect(() => {
    setCustomFormValidation(
      (): ((schema: ZodSchema) => ZodSchema) => (schema) => {
        const hiddenFieldIds = getHiddenFieldIds({
          hiddenFields,
          hiddenSections,
          formSections,
        });

        const requiredFields = getRequiredFields({
          allFieldIds,
          defaultRequiredFields,
          fieldConfig: formFieldConfigurations ?? [],
          hiddenFields: hiddenFieldIds,
        });

        return addRequireFieldsToSchema(schema, requiredFields);
      }
    );
  }, [
    formFieldConfigurations,
    formSections,
    setCustomFormValidation,
    defaultRequiredFields,
    control,
    allFieldIds,
    parentType,
    hiddenFields,
    hiddenSections,
  ]);
};
